/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toET } from '../utils/et';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay, isToday, parseISO } from 'date-fns';
import AssignmentPanel from '../components/AssignmentPanel';
import EditTripModal from '../components/EditTripModal';
import openFile from '../utils/openFile';

const STATUS_COLORS = {
  pending_approval: { bg: '#FAEEDA', color: '#854F0B', label: 'Pending' },
  approved:         { bg: '#E6F1FB', color: '#185FA5', label: 'Approved' },
  dispatched:       { bg: '#E1F5EE', color: '#0F6E56', label: 'Dispatched' },
  completed:        { bg: '#f0f0f0', color: '#666',    label: 'Done' },
  cancelled:        { bg: '#FCEBEB', color: '#A32D2D', label: 'Cancelled' },
};

const PATIENT_TYPE_COLORS = {
  '201':      { bg: '#FAECE7', color: '#993C1D' },
  '302':      { bg: '#FCEBEB', color: '#A32D2D' },
  'standard': { bg: '#E6F1FB', color: '#185FA5' },
  'medical':  { bg: '#EEEDFE', color: '#534AB7' },
};

const isSingleDriverFacility = (transport) => {
  const pt = (transport.patient_type || '').toLowerCase();
  if (pt === '201' || pt === '302' || pt === '303' || pt === '304' || pt === 'bh' || pt === 'da') return false;
  const originFacility = (transport.origin_facility_name || '').toLowerCase();
  const origin = (transport.origin_address || '').toLowerCase();
  if (originFacility) {
    return originFacility.includes('willow valley') || originFacility.includes('lrh') || originFacility.includes('lancaster rehab');
  }
  return origin.includes('willow valley') || origin.includes('good dr') || origin.includes('lancaster rehab');
};

function Badge({ bg, color, label }) {
  return (
    <span style={{ background: bg, color, fontSize: 10, padding: '2px 6px', borderRadius: 20, fontWeight: 500, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

export default function Schedule() {
  const [weekStart, setWeekStart] = useState(startOfWeek(toET(new Date()), { weekStartsOn: 1 }));
  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(toET(new Date()));
  const [assigningTrip, setAssigningTrip] = useState(null);
  const [editingTrip, setEditingTrip] = useState(null);
  // Future days: return legs fold into their outbound card so the list reads
  // one line per appointment and nobody dispatches the will-call return by
  // mistake (Paula 2026-10-14). Today and past days are unchanged.
  const [showReturnsFull, setShowReturnsFull] = useState(false);
  const [expandedReturns, setExpandedReturns] = useState(() => new Set());

  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const fetchWeek = async () => {
    setLoading(true);
    try {
      const start = format(weekStart, 'yyyy-MM-dd');
      const end = format(weekEnd, 'yyyy-MM-dd');
      const res = await api.get(`/transports?startDate=${start}&endDate=${end}`);
      setTransports(res.data.transports || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWeek(); }, [weekStart]);

  const tripsForDay = (day) =>
    transports.filter(t => t.scheduled_time && isSameDay(parseISO(t.scheduled_time), day) && t.status !== 'cancelled');

  const allDayTrips = tripsForDay(selectedDay).sort((a, b) =>
    new Date(a.scheduled_time) - new Date(b.scheduled_time)
  );
  const isFutureDay = (() => { const t = toET(new Date()); t.setHours(0, 0, 0, 0); const d = new Date(selectedDay); d.setHours(0, 0, 0, 0); return d > t; })();
  const dayIds = new Set(allDayTrips.map(t => t.id));
  // A return leg folds under its outbound when both are on this day and we are
  // looking ahead (unless the dispatcher asked for full cards or expanded it).
  const foldedReturnIds = new Set(
    isFutureDay && !showReturnsFull
      ? allDayTrips.filter(t => t.return_leg_of && dayIds.has(t.return_leg_of) && !expandedReturns.has(t.id)).map(t => t.id)
      : []
  );
  const returnsOf = (trip) => allDayTrips.filter(t => t.return_leg_of === trip.id && foldedReturnIds.has(t.id));
  const selectedDayTrips = allDayTrips.filter(t => !foldedReturnIds.has(t.id));

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Schedule</h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
            {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setWeekStart(w => subWeeks(w, 1))} style={{ fontSize: 12, padding: '4px 10px' }}>← Prev</button>
          <button onClick={() => { setWeekStart(startOfWeek(toET(new Date()), { weekStartsOn: 1 })); setSelectedDay(toET(new Date())); }} style={{ fontSize: 12, padding: '4px 10px' }}>This week</button>
          <button onClick={() => setWeekStart(w => addWeeks(w, 1))} style={{ fontSize: 12, padding: '4px 10px' }}>Next →</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 6, marginBottom: 16 }}>
        {days.map(day => {
          const dayTrips = tripsForDay(day);
          const isSelected = isSameDay(day, selectedDay);
          const today = isToday(day);
          return (
            <div
              key={day.toISOString()}
              onClick={() => setSelectedDay(day)}
              style={{
                borderRadius: 10, padding: '10px 8px', cursor: 'pointer', textAlign: 'center',
                border: `0.5px solid ${isSelected ? '#185FA5' : '#e0e0db'}`,
                background: isSelected ? '#E6F1FB' : today ? '#f9f9f8' : 'white',
              }}
            >
              <div style={{ fontSize: 11, color: isSelected ? '#185FA5' : '#999', marginBottom: 2 }}>
                {format(day, 'EEE')}
              </div>
              <div style={{
                fontSize: 16, fontWeight: today ? 500 : 400,
                color: isSelected ? '#185FA5' : today ? '#185FA5' : '#333',
                marginBottom: 6,
              }}>
                {format(day, 'd')}
              </div>
              {dayTrips.length > 0 ? (
                <div style={{
                  fontSize: 11, fontWeight: 500,
                  color: isSelected ? '#185FA5' : '#1D9E75',
                  background: isSelected ? '#B5D4F4' : '#E1F5EE',
                  borderRadius: 20, padding: '2px 6px',
                  display: 'inline-block',
                }}>
                  {dayTrips.length}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: '#ccc' }}>—</div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>
          {format(selectedDay, 'EEEE, MMMM d')}
          {isToday(selectedDay) && <span style={{ fontSize: 11, color: '#1D9E75', marginLeft: 8, fontWeight: 400 }}>Today</span>}
          {isFutureDay && allDayTrips.some(t => t.return_leg_of && dayIds.has(t.return_leg_of)) && (
            <label style={{ fontSize: 11, color: '#666', marginLeft: 12, fontWeight: 400, cursor: 'pointer' }}>
              <input type="checkbox" checked={showReturnsFull} onChange={e => setShowReturnsFull(e.target.checked)} style={{ marginRight: 4 }} />
              show return legs as full cards
            </label>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#999' }}>
          {selectedDayTrips.length} trips — {selectedDayTrips.filter(t => !t.requires_wheelchair).length} sedan / {selectedDayTrips.filter(t => t.requires_wheelchair).length} wheelchair
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>
      ) : selectedDayTrips.length === 0 ? (
        <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#999', fontSize: 13 }}>
          No trips scheduled for this day
        </div>
      ) : (
        <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'hidden' }}>
          {selectedDayTrips.map((trip, i) => {
            const status = STATUS_COLORS[trip.status] || STATUS_COLORS.approved;
            const ptype = PATIENT_TYPE_COLORS[trip.patient_type];
            const estReturn = trip.estimated_return_time
              ? new Date(trip.estimated_return_time)
              : trip.scheduled_time
              ? new Date(new Date(trip.scheduled_time).getTime() + (parseFloat(trip.round_trip_miles) || 40) * 1.5 * 60000)
              : null;
            const vehicleName = trip.vehicle_name || (trip.vehicle_id ? 'Vehicle assigned' : null);

            return (
              <div key={trip.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '12px 16px',
                borderBottom: i < selectedDayTrips.length - 1 ? '0.5px solid #f0f0f0' : 'none',
              }}>
                <div style={{ minWidth: 80 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    {format(toET(trip.scheduled_time), 'h:mm a')}
                  </div>
                  {estReturn && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                      ↩ {format(toET(estReturn), 'h:mm a')}
                    </div>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>
                    {trip.destination_address || 'Destination TBD'}
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                    From: {trip.origin_address || 'TBD'}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {ptype && <Badge bg={ptype.bg} color={ptype.color} label={trip.patient_type === '201' ? 'BH 201' : trip.patient_type === '302' ? 'BH 302' : trip.patient_type} />}
                    {trip.requires_wheelchair ? <Badge bg="#EEEDFE" color="#3C3489" label="Wheelchair" /> : <Badge bg="#E6F1FB" color="#185FA5" label="Ambulatory" />}
                    {trip.request_channel === 'form' && <Badge bg="#FFF7E6" color="#854F0B" label="Facility portal" />}
                    {trip.request_channel === 'fax' && <Badge bg="#f0f0f0" color="#666" label="Fax" />}
                    {trip.request_channel === 'imessage' && <Badge bg="#E1F5EE" color="#0F6E56" label="Chat" />}
                    <Badge bg="#f0f0f0" color="#666" label={trip.transport_number} />
                    {trip.fax_file_id && (
                      <a href="#" onClick={(e) => openFile(trip.fax_file_id, e)}
                        style={{ fontSize: 10, color: '#185FA5', textDecoration: 'none', padding: '2px 7px', background: '#E6F1FB', borderRadius: 20 }}>
                        View fax
                      </a>
                    )}
                    {trip.attachment_file_id && (
                      <a href="#" onClick={(e) => openFile(trip.attachment_file_id, e)}
                        style={{ fontSize: 10, color: '#185FA5', textDecoration: 'none', padding: '2px 7px', background: '#E6F1FB', borderRadius: 20 }}>
                        Attachment
                      </a>
                    )}
                  </div>
                  {trip.notes && (() => {
                    const parts = (trip.notes || '').split('|').map(s => s.trim()).filter(Boolean);
                    const patient = parts.find(p => p.startsWith('Patient:'));
                    const requester = parts.find(p => p.startsWith('Requester:'));
                    const email = parts.find(p => p.startsWith('Email:'));
                    const room = parts.find(p => p.startsWith('Room:'));
                    const billTo = parts.find(p => p.startsWith('Bill to:'));
                    const hasInfo = patient || requester || room || billTo;
                    if (!hasInfo) return null;
                    return (
                      <div style={{ fontSize: 11, color: '#666', marginTop: 4, background: '#f9f9f8', padding: '4px 8px', borderRadius: 6 }}>
                        {patient && <span style={{ marginRight: 8 }}>{patient}</span>}
                        {room && <span style={{ marginRight: 8 }}>{room}</span>}
                        {requester && <span style={{ marginRight: 8 }}>{requester}</span>}
                        {email && <span style={{ marginRight: 8, color: '#185FA5' }}>{email}</span>}
                        {billTo && <span style={{ fontWeight: 500, color: '#854F0B' }}>{billTo}</span>}
                      </div>
                    );
                  })()}
                  {(vehicleName || (trip.assignees || []).length > 0) && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4, alignItems: 'center' }}>
                      {isSingleDriverFacility(trip) && (trip.assignees || []).length === 1 && (
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#FCEBEB', color: '#A32D2D', fontWeight: 700, border: '1.5px solid #F09595' }}>
                          ONE DRIVER ONLY
                        </span>
                      )}
                      {vehicleName && <Badge bg="#f5f5f3" color="#444" label={'🚐 ' + vehicleName} />}
                      {(trip.assignees || []).map((a, i) => (
                        <Badge key={i} bg={i === 0 ? '#E1F5EE' : '#E6F1FB'} color={i === 0 ? '#085041' : '#0C447C'} label={a.name} />
                      ))}
                    </div>
                  )}
                  {returnsOf(trip).map(ret => {
                    const rs = STATUS_COLORS[ret.status] || STATUS_COLORS.approved;
                    return (
                      <div key={ret.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6, padding: '5px 8px', background: '#FAFAF8', border: '0.5px dashed #d8d8d2', borderRadius: 6, fontSize: 11, color: '#666' }}>
                        <span style={{ fontWeight: 600, color: '#854F0B' }}>↩ Return</span>
                        <span>{ret.is_will_call ? 'Will call' : format(toET(ret.scheduled_time), 'h:mm a')}</span>
                        <span style={{ color: '#999' }}>{ret.transport_number}</span>
                        <Badge {...rs} />
                        {(ret.assignees || []).length > 0 && <span>{(ret.assignees || []).map(a => a.name.split(' ')[0]).join(' & ')}</span>}
                        <button onClick={() => setExpandedReturns(prev => { const n = new Set(prev); n.add(ret.id); return n; })}
                          style={{ fontSize: 10, padding: '1px 7px', marginLeft: 'auto', background: '#fff', color: '#666', border: '0.5px solid #d8d8d2', borderRadius: 4 }}>
                          Open
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <Badge {...status} />
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <button onClick={() => setEditingTrip(trip)}
                      style={{ fontSize: 10, padding: '2px 8px', background: '#E6F1FB', color: '#185FA5', border: '0.5px solid #B5D4F4', borderRadius: 4 }}>
                      Edit
                    </button>
                    <button onClick={() => setAssigningTrip(trip)}
                      style={{ fontSize: 10, padding: '2px 8px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5', borderRadius: 4 }}>
                      {trip.status === 'completed' ? 'Edit crew' : 'Reassign'}
                    </button>
                  </div>
                  {['approved','dispatched','en_route_pickup','picked_up','dropped_off'].includes(trip.status) && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <button onClick={async () => {
                        if (!window.confirm(`Cancel ${trip.transport_number}?`)) return;
                        // Round trip: offer the whole thing at once (Paula 2026-09-25 --
                        // leg-by-leg from here cancelled one leg of the wrong trip).
                        const live = t => t && !['cancelled', 'completed', 'no_show'].includes(t.status);
                        const sib = transports.find(t => t.id !== trip.id && ((trip.return_leg_of && t.id === trip.return_leg_of) || t.return_leg_of === trip.id));
                        let includeSibling = false;
                        if (live(sib)) {
                          const when = sib.scheduled_time ? format(toET(sib.scheduled_time), 'EEE h:mm a') : '';
                          includeSibling = window.confirm(`This is a round trip. Also cancel the other leg, ${sib.transport_number}${when ? ' (' + when + ')' : ''}?\n\nOK = cancel BOTH legs.   Cancel = only ${trip.transport_number}.`);
                        }
                        const body = { reason: 'Admin cancel from schedule', includeSibling };
                        try {
                          await api.put(`/transports/${trip.id}/cancel`, body);
                          fetchWeek();
                        } catch (e) {
                          // 409 = quoted/prepaid booking: show the money warning, retry acknowledged
                          if (e.response?.status === 409 && e.response.data?.requiresPrepayAck) {
                            if (window.confirm(e.response.data.warning)) {
                              try {
                                await api.put(`/transports/${trip.id}/cancel`, { ...body, prepayAcknowledged: true });
                                fetchWeek();
                              } catch (e2) { alert('Failed: ' + (e2.response?.data?.error || e2.message)); }
                            }
                            return;
                          }
                          alert('Failed: ' + (e.response?.data?.error || e.message));
                        }
                      }} style={{ fontSize: 10, padding: '2px 8px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #F09595', borderRadius: 4 }}>
                        Cancel
                      </button>
                      <button onClick={async () => {
                        if (!window.confirm(`Complete ${trip.transport_number}? Billing/payroll will use Google Maps estimates.`)) return;
                        try {
                          await api.put(`/transports/${trip.id}/admin-complete`);
                          fetchWeek();
                        } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
                      }} style={{ fontSize: 10, padding: '2px 8px', background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', borderRadius: 4 }}>
                        Complete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {assigningTrip && (
        <AssignmentPanel
          transport={assigningTrip}
          onClose={() => setAssigningTrip(null)}
          onDispatched={async () => {
            // Recalculate payroll for the trip after reassignment
            try {
              // If trip is completed, re-run finalize to recalculate pay for all assignees
              if (assigningTrip.status === 'completed') {
                await api.put(`/transports/${assigningTrip.id}/admin-complete`);
              }
            } catch (e) {}
            setAssigningTrip(null);
            fetchWeek();
          }}
          existingTransports={transports}
        />
      )}
      {editingTrip && (
        <EditTripModal
          trip={editingTrip}
          onClose={() => setEditingTrip(null)}
          onSaved={() => { fetchWeek(); setEditingTrip(null); }}
        />
      )}
    </div>
  );
}